import axiosConfig from "./axiosConfig";

// Mirrors the BE `_serialise_operation` shape in
// `lambda_handlers/api/admin/operations.py`. Status-page rows
// surface the criteria, progress, kicker email, and chunk plan.
export interface OperationSummary {
  operationKey: string;
  operationType: string;
  siteId: string | null;
  // pending → state machine starting up / sync Lambda invoked
  // running → at least one chunk done, or sync worker mid-stage
  // completed → all chunks done (chunked) or worker finished (sync)
  // cancelled → operator clicked Cancel; some chunks may have
  //             completed before the StopExecution landed
  // failed → state machine or sync worker hit an unhandled error
  status: "pending" | "running" | "completed" | "cancelled" | "failed";
  kickedOffBy: string | null;
  // Unix epoch seconds.
  createdAt: number | null;
  updatedAt: number | null;
  cancelRequestedAt: number | null;
  // Original search criteria (postcodeOutward, minFloorAreaSqm,
  // radiusKm, etc.). The decoded auth token is stripped before
  // serialisation.
  criteria: Record<string, unknown> | null;
  // The progress blob the worker writes per stage / per chunk.
  progress: {
    current: number;
    total: number;
    message: string;
    processed?: number;
    totalInStage?: number;
  } | null;
  result: Record<string, unknown> | null;
  // Total-matched rows from the SQL filter, persisted at search-
  // creation time. Drives the "Results" column on the status page
  // for both sync and chunked operations.
  rowsMatched: number;
  // Chunked-only: how many chunks the search will run, how many
  // are done, and the chunk size in rows. Zero for sync operations
  // (the chunk plan only applies to the Step Functions path).
  chunksTotal: number;
  chunksCompleted: number;
  rowsTotal: number;
  chunkSize: number;
  failedRowCount: number;
  failedRowRefs: string[];
  error: string | null;
}

export interface ListOperationsResponse {
  operations: OperationSummary[];
}

export interface CancelOperationResponse {
  operation: OperationSummary;
}

const getOperations = async (
  authToken: string,
  params: { type?: string; limit?: number } = {}
): Promise<ListOperationsResponse> => {
  const { data } = await axiosConfig.get("/operations", {
    headers: { Authorization: `Bearer ${authToken}` },
    params,
  });
  return data;
};

export interface GetOperationResponse {
  operation: OperationSummary;
}

// Single-operation polling endpoint. DDB primary key is
// (siteId, operationKey), so we have to pass siteId. The FE got it
// from the original POST response (`/leads/bulk-create-sites` returns
// `{operationKey, siteId, ...}`). Used by the bulk-create modal so we
// don't hammer the list endpoint when we know exactly which row we
// want.
export const getOperationByKey = async (
  authToken: string,
  operationKey: string,
  siteId: string
): Promise<GetOperationResponse> => {
  const { data } = await axiosConfig.get(
    `/operations/${encodeURIComponent(operationKey)}`,
    {
      headers: { Authorization: `Bearer ${authToken}` },
      params: { siteId },
    }
  );
  return data;
};

export const postCancelOperation = async (
  authToken: string,
  operationKey: string
): Promise<CancelOperationResponse> => {
  const { data } = await axiosConfig.post(
    `/operations/${encodeURIComponent(operationKey)}/cancel`,
    {},
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export interface DeleteOperationResponse {
  deleted: boolean;
  operationKey: string;
}

export const deleteOperation = async (
  authToken: string,
  operationKey: string
): Promise<DeleteOperationResponse> => {
  const { data } = await axiosConfig.delete(
    `/operations/${encodeURIComponent(operationKey)}`,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export default getOperations;
